import React, { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { 
  ShoppingCart, Plus, Truck, Package, AlertTriangle, 
  CheckCircle2, Clock, FileText, Send, Eye, X, Download,
  ChevronDown, ChevronUp, RefreshCw, Search
} from 'lucide-react';

const STATUS_CONFIG = {
  draft: { bg: 'bg-slate-100 text-slate-700', icon: FileText, label: 'Ciorna' },
  pending: { bg: 'bg-yellow-100 text-yellow-700', icon: Clock, label: 'In asteptare' },
  sent: { bg: 'bg-blue-100 text-blue-700', icon: Send, label: 'Trimisa' },
  partial: { bg: 'bg-orange-100 text-orange-700', icon: Package, label: 'Partial receptionata' },
  received: { bg: 'bg-green-100 text-green-700', icon: CheckCircle2, label: 'Receptionata' },
  cancelled: { bg: 'bg-red-100 text-red-700', icon: X, label: 'Anulata' }
};

const OrdersTab = () => {
  const [suggestions, setSuggestions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewOrderDialog, setShowNewOrderDialog] = useState(false);
  const [selectedItems, setSelectedItems] = useState([]);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  
  const [newOrderForm, setNewOrderForm] = useState({
    supplier_id: '',
    notes: ''
  });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [suggestionsRes, ordersRes, suppliersRes] = await Promise.all([
        api.get('/wms/purchase-suggestions'),
        api.get('/wms/purchase-orders'),
        api.get('/suppliers')
      ]);
      setSuggestions(suggestionsRes.data);
      setOrders(ordersRes.data);
      setSuppliers(suppliersRes.data);
    } catch {
      toast.error('Eroare la incarcarea datelor');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrder = async () => {
    if (selectedItems.length === 0) { toast.error('Selectati cel putin un articol'); return; }
    try {
      await api.post('/wms/purchase-orders', {
        supplier_id: newOrderForm.supplier_id || null,
        items: selectedItems.map(item => ({
          article_id: item.article_id,
          article_name: item.article_name,
          article_code: item.article_code,
          quantity: item.suggested_quantity,
          unit: item.unit
        })),
        notes: newOrderForm.notes
      });
      toast.success('Comanda creata cu succes!');
      setShowNewOrderDialog(false);
      setNewOrderForm({ supplier_id: '', notes: '' });
      setSelectedItems([]);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Eroare la creare comanda');
    }
  };

  const handleUpdateOrderStatus = async (orderId, newStatus) => {
    try {
      await api.put(`/wms/purchase-orders/${orderId}/status`, { status: newStatus });
      toast.success('Status actualizat!');
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Eroare la actualizare status');
    }
  };

  const toggleItemSelection = (item) => {
    setSelectedItems(prev => {
      const exists = prev.find(i => i.article_id === item.article_id);
      if (exists) return prev.filter(i => i.article_id !== item.article_id);
      return [...prev, { ...item, suggested_quantity: item.to_order }];
    });
  };

  const updateItemQuantity = (articleId, quantity) => {
    setSelectedItems(prev => prev.map(item => 
      item.article_id === articleId ? { ...item, suggested_quantity: parseFloat(quantity) || 0 } : item
    ));
  };

  const handleExportOrder = (order) => {
    try {
      const rows = (order.items || []).map((item, idx) => ({
        '#': idx + 1,
        'Cod Articol': item.article_code || '-',
        'Denumire': item.article_name,
        'Cantitate': item.quantity,
        'UM': item.unit || 'BUC'
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      ws['!cols'] = [{ wch: 5 }, { wch: 15 }, { wch: 40 }, { wch: 12 }, { wch: 8 }];
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Comanda');
      XLSX.writeFile(wb, `comanda_${order.order_number}.xlsx`);
      toast.success('Comanda exportata!');
    } catch {
      toast.error('Eroare la export');
    }
  };

  const handleExportAllOrders = () => {
    try {
      const rows = filteredOrders.map(o => ({
        'Nr. Comanda': o.order_number,
        'Furnizor': o.supplier_name || '-',
        'Articole': o.items?.length || 0,
        'Status': STATUS_CONFIG[o.status]?.label || o.status,
        'Data': new Date(o.created_at).toLocaleDateString('ro-RO'),
        'Note': o.notes || ''
      }));
      const ws = XLSX.utils.json_to_sheet(rows);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Comenzi');
      XLSX.writeFile(wb, `comenzi_furnizori_${new Date().toISOString().split('T')[0]}.xlsx`);
      toast.success('Export generat!');
    } catch {
      toast.error('Eroare la export');
    }
  };

  const getStatusBadge = (status) => {
    const style = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
    const Icon = style.icon;
    return <Badge className={style.bg}><Icon className="w-3 h-3 mr-1" />{style.label}</Badge>;
  };

  const filteredOrders = orders.filter(o => {
    if (statusFilter !== 'all' && o.status !== statusFilter) return false;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      return o.order_number?.toLowerCase().includes(q) || 
             o.supplier_name?.toLowerCase().includes(q) ||
             o.items?.some(i => i.article_name?.toLowerCase().includes(q));
    }
    return true;
  });

  return (
    <div className="space-y-6" data-testid="wms-orders-page">
      {/* Purchase Suggestions */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Sugestii Aprovizionare</h3>
            <p className="text-sm text-slate-500">{suggestions.length} articole sub stoc minim</p>
          </div>
          {suggestions.length > 0 && (
            <Button onClick={() => setShowNewOrderDialog(true)} size="sm">
              <ShoppingCart className="w-4 h-4 mr-1" /> Creeaza Comanda ({selectedItems.length})
            </Button>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-6"><RefreshCw className="w-6 h-6 animate-spin text-blue-500" /></div>
        ) : suggestions.length === 0 ? (
          <Card><CardContent className="py-8 text-center">
            <CheckCircle2 className="w-10 h-10 mx-auto text-green-500 mb-2" />
            <p className="text-slate-600">Toate articolele sunt in stoc!</p>
          </CardContent></Card>
        ) : (
          <div className="grid gap-2">
            {suggestions.map(item => {
              const selected = selectedItems.find(i => i.article_id === item.article_id);
              return (
                <div
                  key={item.article_id}
                  onClick={() => toggleItemSelection(item)}
                  className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${
                    selected ? 'ring-2 ring-blue-500 bg-blue-50 border-blue-300' : 'border-slate-200 hover:border-blue-200 hover:bg-slate-50'
                  }`}
                  data-testid={`suggestion-${item.article_id}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded flex items-center justify-center ${item.current_stock === 0 ? 'bg-red-100' : 'bg-orange-100'}`}>
                      <AlertTriangle className={`w-4 h-4 ${item.current_stock === 0 ? 'text-red-600' : 'text-orange-600'}`} />
                    </div>
                    <div>
                      <p className="font-medium text-sm text-slate-900">{item.article_name}</p>
                      <p className="text-xs text-slate-500">{item.article_code}</p>
                    </div>
                  </div>
                  <div className="text-right text-xs">
                    <p className="text-slate-500">Stoc: <span className="font-bold text-red-600">{item.current_stock}</span> / Min: {item.min_stock}</p>
                    <p className="font-medium text-blue-600">Comanda: {item.to_order} {item.unit}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Orders */}
      <div className="space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <h3 className="text-lg font-semibold text-slate-900">Comenzi Furnizori ({filteredOrders.length})</h3>
          <div className="flex gap-2">
            <div className="relative flex-1 sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Cauta comanda..."
                className="pl-10 h-9"
                data-testid="orders-search"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 h-9" data-testid="orders-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toate</SelectItem>
                {Object.entries(STATUS_CONFIG).map(([k, v]) => (
                  <SelectItem key={k} value={k}>{v.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={handleExportAllOrders}>
              <Download className="w-4 h-4 mr-1" /> Export
            </Button>
          </div>
        </div>

        {filteredOrders.length === 0 ? (
          <Card><CardContent className="py-8 text-center">
            <Truck className="w-10 h-10 mx-auto text-slate-300 mb-2" />
            <p className="text-slate-500">Nicio comanda gasita</p>
          </CardContent></Card>
        ) : (
          <div className="space-y-2">
            {filteredOrders.map(order => {
              const isExpanded = expandedOrderId === order.id;
              return (
                <Card key={order.id} className="overflow-hidden">
                  <CardContent className="p-0">
                    {/* Order header */}
                    <div
                      className="flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 transition-colors"
                      onClick={() => setExpandedOrderId(isExpanded ? null : order.id)}
                      data-testid={`order-row-${order.id}`}
                    >
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <Truck className="w-5 h-5 text-blue-600" />
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-bold text-slate-900">{order.order_number}</p>
                            {getStatusBadge(order.status)}
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {order.supplier_name || 'Fara furnizor'} | {order.items?.length || 0} articole | {new Date(order.created_at).toLocaleDateString('ro-RO')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" onClick={(e) => { e.stopPropagation(); handleExportOrder(order); }} data-testid={`export-order-${order.id}`}>
                          <Download className="w-3 h-3 mr-1" /> Excel
                        </Button>
                        {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
                      </div>
                    </div>

                    {/* Expanded detail */}
                    {isExpanded && (
                      <div className="border-t bg-slate-50 p-4 space-y-4">
                        {/* Order Items Table */}
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="border-b">
                                <th className="text-left p-2 text-xs font-semibold text-slate-500">#</th>
                                <th className="text-left p-2 text-xs font-semibold text-slate-500">Cod</th>
                                <th className="text-left p-2 text-xs font-semibold text-slate-500">Denumire Articol</th>
                                <th className="text-right p-2 text-xs font-semibold text-slate-500">Cantitate</th>
                                <th className="text-center p-2 text-xs font-semibold text-slate-500">UM</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(order.items || []).map((item, idx) => (
                                <tr key={idx} className="border-b border-slate-100">
                                  <td className="p-2 text-slate-400">{idx + 1}</td>
                                  <td className="p-2 font-mono text-xs">{item.article_code || '-'}</td>
                                  <td className="p-2 font-medium">{item.article_name}</td>
                                  <td className="p-2 text-right font-bold">{item.quantity}</td>
                                  <td className="p-2 text-center text-slate-500">{item.unit || 'BUC'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {order.notes && (
                          <div className="text-sm"><span className="text-slate-500">Note:</span> {order.notes}</div>
                        )}

                        {/* Actions */}
                        <div className="flex gap-2 pt-2 border-t">
                          {order.status === 'draft' && (
                            <>
                              <Button variant="outline" size="sm" onClick={() => handleUpdateOrderStatus(order.id, 'cancelled')} className="text-red-600">
                                <X className="w-3 h-3 mr-1" /> Anuleaza
                              </Button>
                              <Button size="sm" onClick={() => handleUpdateOrderStatus(order.id, 'sent')} className="bg-blue-600 hover:bg-blue-700">
                                <Send className="w-3 h-3 mr-1" /> Marcheaza Trimisa
                              </Button>
                            </>
                          )}
                          {order.status === 'sent' && (
                            <Button size="sm" onClick={() => handleUpdateOrderStatus(order.id, 'received')} className="bg-green-600 hover:bg-green-700">
                              <CheckCircle2 className="w-3 h-3 mr-1" /> Marcheaza Receptionata
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* New Order Dialog */}
      <Dialog open={showNewOrderDialog} onOpenChange={setShowNewOrderDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Comanda Noua</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Furnizor</Label>
              <Select value={newOrderForm.supplier_id} onValueChange={v => setNewOrderForm({ ...newOrderForm, supplier_id: v })}>
                <SelectTrigger><SelectValue placeholder="Selecteaza furnizor (optional)" /></SelectTrigger>
                <SelectContent>
                  {suppliers.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Articole selectate ({selectedItems.length})</Label>
              {selectedItems.length === 0 ? (
                <p className="text-sm text-slate-500 p-3 bg-slate-50 rounded">Selectati articole din sugestii</p>
              ) : (
                <div className="space-y-1 max-h-[250px] overflow-y-auto">
                  {selectedItems.map(item => (
                    <div key={item.article_id} className="flex items-center justify-between p-2 bg-slate-50 rounded text-sm">
                      <div><p className="font-medium">{item.article_name}</p><p className="text-xs text-slate-500">{item.article_code}</p></div>
                      <div className="flex items-center gap-2">
                        <Input type="number" className="w-20 h-8 text-sm" value={item.suggested_quantity} onChange={(e) => updateItemQuantity(item.article_id, e.target.value)} min="1" />
                        <span className="text-xs text-slate-500">{item.unit}</span>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => toggleItemSelection(item)}><X className="w-3 h-3 text-red-500" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea value={newOrderForm.notes} onChange={(e) => setNewOrderForm({ ...newOrderForm, notes: e.target.value })} placeholder="Note..." rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewOrderDialog(false)}>Anuleaza</Button>
            <Button onClick={handleCreateOrder} disabled={selectedItems.length === 0}>
              <ShoppingCart className="w-4 h-4 mr-2" /> Creeaza Comanda
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrdersTab;
